import { Reveal } from "@/components/Reveal";
import { CTA, Eyebrow } from "@/components/ui-kit";
import { PageHero } from "@/components/PageHero";
import { IMAGES, BUILDER_ATTRIBUTES } from "@/lib/content";

const FRAMEWORK = [
  { step: "01", title: "Foundation", desc: "Character first. Everything worth building rests on who you are when no one is watching." },
  { step: "02", title: "Framing", desc: "Purpose and direction. You decide what you’re building and why it matters." },
  { step: "03", title: "Building", desc: "Discipline and stewardship applied daily across every area of your life." },
  { step: "04", title: "Legacy", desc: "Impact that outlives you — a life that leaves the world better than you found it." },
];

export default function Philosophy() {
  return (
    <div data-testid="philosophy-page">
      <PageHero
        eyebrow="The Builder Philosophy"
        title="Money is a result. Character is the foundation."
        subtitle="People don’t need another motivational speaker, hustle coach, or business guru. They need a better foundation. The Builder philosophy is that foundation."
        testId="philosophy-hero"
      />

      {/* WHAT A BUILDER BUILDS */}
      <section className="bg-white text-black py-28 lg:py-36">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Eyebrow className="!text-[#B8952E]">The Identity</Eyebrow>
            <h2 className="font-display text-4xl lg:text-5xl mt-6 tracking-tight">A Builder Builds…</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {BUILDER_ATTRIBUTES.map((a, i) => (
              <Reveal key={a.title} delay={i * 55}>
                <div className="h-full border border-[#e5e5e5] p-8 hover:border-[#D4AF37] hover:-translate-y-1 transition-all duration-300">
                  <span className="font-display text-[#B8952E] text-2xl">0{i + 1}</span>
                  <h3 className="font-display text-2xl mt-5">{a.title}</h3>
                  <p className="font-body text-sm text-black/55 mt-3 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE FRAMEWORK */}
      <section
        className="relative bg-cover bg-center py-28 lg:py-40"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.85),rgba(0,0,0,0.9)), url(${IMAGES.foundation})` }}
      >
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Eyebrow>The Builder Framework</Eyebrow>
            <h2 className="font-display text-4xl lg:text-5xl text-white mt-6 tracking-tight">
              Every meaningful life is built in order.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {FRAMEWORK.map((f, i) => (
              <Reveal key={f.step} delay={i * 80}>
                <div className="border-t border-[#D4AF37]/40 pt-7">
                  <span className="font-display text-[#D4AF37] text-4xl">{f.step}</span>
                  <h3 className="font-display text-2xl text-white mt-4">{f.title}</h3>
                  <p className="font-body text-sm text-white/55 mt-3 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black noise py-28 lg:py-36">
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-display text-3xl lg:text-4xl text-white leading-snug">
              The philosophy begins in the book.<br />
              <span className="text-[#D4AF37] italic">Living it begins with you.</span>
            </p>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-11 flex justify-center gap-4 flex-wrap">
              <CTA to="/checkout" track="get_the_shift" location="philosophy_cta">Get The Shift</CTA>
              <CTA to="/system" variant="ghost-light" arrow={false}>Explore The System</CTA>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
